import { InputWithSelection } from '@components/input';
import { useGeoDataContext } from '@contexts/GeoDataContext.tsx';
import { Loader, Typography } from '@gurupay/gp-ui-library';
import { useDebounce } from '@hooks/useDebounce.ts';
import { useFxQuote } from '@hooks/useFxQuotes.ts';
import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { FXFormValues } from '../FXForm';

interface TransferInputProps {
	accountId?: string;
	isoCodes: { value: string; label: string }[];
	isDisabled?: boolean;
	isReinit?: boolean;
}

export const TransferInput: FC<TransferInputProps> = ({
	accountId,
	isoCodes,
	isDisabled,
	isReinit = false,
}) => {
	const geoData = useGeoDataContext();
	const currencies = useMemo(
		() =>
			geoData?.data?.currencies.map((c) => ({ ...c, label: c.label.toUpperCase() })) ??
			[],
		[geoData?.data?.currencies],
	);
	const { t } = useTranslation('translation', {
		keyPrefix: 'payments.newPayment.additionalData.fx',
	});
	const {
		watch,
		setValue,
		formState: { submitCount },
	} = useFormContext<FXFormValues>();

	const [amount, fxAmount, fxCurrency, currency] = watch([
		'amount',
		'fx_amount',
		'fx_currency',
		'currency',
	]);

	// Tracks which input the user changed last; undefined = rate-display only (no form updates)
	const lastChanged = useRef<'amount' | 'fxAmount' | undefined>(
		isReinit ? 'amount' : undefined,
	);

	const debouncedAmount = useDebounce(amount);
	const debouncedFxAmount = useDebounce(fxAmount);

	useEffect(() => {
		if (!currency && currencies[0]?.value) {
			setValue('currency', currencies[0].value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currencies, currency]);

	const fxQuotePayload = useMemo(() => {
		if (!fxCurrency || !accountId) {
			return undefined;
		}
		const isReverse = lastChanged.current === 'fxAmount';
		const checkedAmount = isReverse ? debouncedFxAmount : debouncedAmount;
		return {
			amount: (checkedAmount ?? 1) * 100,
			currency: fxCurrency,
			account_uuid: accountId,
			is_reverse: !!checkedAmount && isReverse,
		};
	}, [debouncedAmount, debouncedFxAmount, fxCurrency, accountId]);

	const { data: fxQuote, isLoading, refetch } = useFxQuote(fxQuotePayload);

	const prevSubmitCount = useRef(submitCount);

	useEffect(() => {
		if (prevSubmitCount.current > 0 && submitCount === 0) {
			if (isReinit) {
				lastChanged.current = 'amount';
				void refetch();
			} else {
				lastChanged.current = undefined;
				setValue('fx_amount', undefined);
			}
		}
		prevSubmitCount.current = submitCount;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submitCount]);

	useEffect(() => {
		const isReverse = lastChanged.current === 'fxAmount';
		const checkedAmount = isReverse ? debouncedFxAmount : debouncedAmount;
		// Drop the quote whenever there is no sending amount so the form is not
		// submittable with an empty value, even while a quote is refetching.
		if (!checkedAmount) {
			setValue('fx_quote', undefined);
			if (lastChanged.current) {
				setValue(isReverse ? 'amount' : 'fx_amount', undefined);
			}
			return;
		}
		if (!fxQuote) {
			return;
		}
		// Commit the quote id even before the user touches an input so the
		// prefilled amount is submittable on load.
		setValue('fx_quote', fxQuote.id);
		if (!lastChanged.current) {
			return;
		}
		if (fxQuotePayload?.is_reverse) {
			setValue('amount', fxQuote.amount_from / 100);
		} else {
			setValue('fx_amount', fxQuote.amount_to / 100);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fxQuote?.id, debouncedAmount, debouncedFxAmount]);

	return (
		<>
			<Typography level="h4">{t('sendingAmount')}</Typography>
			<InputWithSelection
				inputName="amount"
				isDisabled={isDisabled}
				onUserChange={() => {
					lastChanged.current = 'amount';
				}}
				options={currencies}
				selectorName="currency"
			/>
			{isLoading && <Loader />}
			{fxQuote && (
				<Typography className="text-brand-secondary-600 text-sm-bold">
					1 {fxQuote.currency_from} = {fxQuote.customer_rate} {fxQuote.currency_to}*
				</Typography>
			)}
			<Typography level="h4">{t('receivingAmount')}</Typography>
			<InputWithSelection
				inputName="fx_amount"
				isDisabled={isDisabled}
				onUserChange={() => {
					lastChanged.current = 'fxAmount';
				}}
				options={isoCodes}
				selectorName="fx_currency"
			/>
		</>
	);
};
